import { Controller } from 'stimulus';
import Rails from '@rails/ujs';

export default class extends Controller {
  static targets = ['content'];
  static outlets = ['mobile-toolbar'];

  connect() {
    this._boundSwap = this._swap.bind(this);
    document.addEventListener('promote:content-page-swap', this._boundSwap);
  }

  disconnect() {
    document.removeEventListener('promote:content-page-swap', this._boundSwap);
  }

  // Whatever is on screen gets a chance to hold the learner back before its
  // content is thrown away, since leaving here is a click, not a navigation,
  // and never reaches beforeunload.
  restore() {
    if (this._originalHtml === undefined) return;

    const beforeLeave = new CustomEvent('promote:before-leave-content', {
      cancelable: true,
      detail: { proceed: () => this._restore(), restoresSnapshot: true }
    });

    if (document.dispatchEvent(beforeLeave)) this._restore();
  }

  _restore() {
    this.contentTarget.innerHTML = this._originalHtml;
    this._originalHtml = undefined;

    const aside = document.getElementById('js-aside');
    if (aside) aside.style.display = '';

    const navSidebar = document.getElementById('js-quiz-nav-sidebar');
    if (navSidebar) {
      navSidebar.classList.remove('xl:block');
      navSidebar.innerHTML = '';
    }

    const toolbar = document.getElementById('js-mobile-toolbar');
    if (toolbar && this._originalToolbar && toolbar !== this._originalToolbar) {
      toolbar.replaceWith(this._originalToolbar);
    }
    this._originalToolbar = undefined;
    this._currentToolbarHtml = undefined;
  }

  _swap(event) {
    const { html, navSidebarHtml, mobileToolbarHtml } = event.detail || {};
    if (typeof html !== 'string') return;

    // Snapshot whatever is on screen right now, not at connect() time, which can go stale since
    // switching tasks via the in-page task list swaps task content without reconnecting this
    // controller.
    if (this._originalHtml === undefined) {
      // The clicked button is still disabled by Rails UJS at this point. This needs to be undone
      // or restore() will reinstate its "Loading..." state.
      this.contentTarget
        .querySelectorAll(`${Rails.formEnableSelector}, ${Rails.linkDisableSelector}`)
        .forEach(el => Rails.enableElement(el));
      this._originalHtml = this.contentTarget.innerHTML;
      this._originalToolbar = document.getElementById('js-mobile-toolbar');
      this._currentToolbarHtml = this._originalToolbar?.outerHTML;
    }

    this.contentTarget.innerHTML = html;

    const aside = document.getElementById('js-aside');
    if (aside) aside.style.display = 'none';

    const navSidebar = document.getElementById('js-quiz-nav-sidebar');
    if (navSidebar && typeof navSidebarHtml === 'string') {
      navSidebar.innerHTML = navSidebarHtml;
      navSidebar.classList.add('xl:block');
    }

    this._swapToolbar(mobileToolbarHtml);

    if (this.hasMobileToolbarOutlet) {
      this.mobileToolbarOutlet.selectMain();
    }
  }

  // The toolbar only needs to change shape when its key actually changes (e.g. opening a
  // questionnaire, or moving from questionnaire to summary) — not on every swap, since most
  // swaps (navigating between questions) leave it in the same state. Comparing the rendered
  // HTML avoids replacing it, and losing the currently active tab, when nothing changed.
  _swapToolbar(mobileToolbarHtml) {
    if (typeof mobileToolbarHtml !== 'string' || mobileToolbarHtml === this._currentToolbarHtml) {
      return;
    }

    const toolbar = document.getElementById('js-mobile-toolbar');
    if (toolbar) {
      toolbar.outerHTML = mobileToolbarHtml;
      this._currentToolbarHtml = mobileToolbarHtml;
    }
  }
}
